import { useCallback, useEffect, useRef, useState } from 'react';

import type { SoftphoneClient, SoftphoneStatus } from './softphoneClient.types';

/**
 * Thrown by `startServerCall` when the server declined to START the call for a benign reason the
 * surface has already handled (e.g. a teammate is already dialing this queued call, so the page has
 * shown a toast and advanced to the next contact). `start()` treats it as a quiet cancel — reset to
 * idle, NOT the error state — so the next contact's dialer isn't left showing a stale error.
 */
export class CallAbortedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CallAbortedError';
  }
}

export interface UseSoftphoneParams {
  /** Provider-specific client, created by the surface (dashboard / extension) so the mic strategy is injected there. */
  client: SoftphoneClient;
  /** Mint a fresh browser token (tRPC `voiceCall.getClientToken`). */
  fetchToken: () => Promise<{ token: string }>;
  /** Create the server-side pending call and resolve the number to dial (tRPC `voiceCall.startCall`). */
  startServerCall: () => Promise<{
    voiceCallId: string;
    destinationNumber: string;
    callerNumber: string | null;
    recordingEnabled?: boolean;
  }>;
}

export interface SoftphoneController {
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  isRecording: boolean;
  voiceCallId: string | null;
  /**
   * The from-number of the in-flight (or just-finished) call, from the startCall response.
   * The server pins a contact's number on first dial, so this is the only place a first-time
   * contact's assigned number is known client-side. Null until startServerCall resolves.
   */
  callerNumber: string | null;
  start: () => Promise<void>;
  hangup: () => void;
  /** Hang up the active call (if any) and reset all call state to idle. Used when the dialer closes. */
  close: () => void;
  toggleMute: () => void;
  /** Send DTMF tones on the live call (keypad / IVR navigation). */
  sendDigits: (digits: string) => void;
}

/** Owns a SoftphoneClient's lifecycle and exposes call state to a presentational component. */
export function useSoftphone({ client, fetchToken, startServerCall }: UseSoftphoneParams): SoftphoneController {
  const [status, setStatus] = useState<SoftphoneStatus>('idle');
  const [durationSec, setDurationSec] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceCallId, setVoiceCallId] = useState<string | null>(null);
  const [callerNumber, setCallerNumber] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Synchronous re-entry guard for start(). `status` can't be used here — two clicks in the same
  // tick both read the pre-`setStatus` value, so the guard must flip before the first `await`.
  const startInFlightRef = useRef(false);

  useEffect(() => {
    client.on({
      onStatus: (s) => setStatus(s),
      onError: (message) => {
        setError(message);
        setStatus('error');
      },
    });
    return () => {
      void client.destroy();
    };
  }, [client]);

  // Tick the call timer only while the call is connected.
  useEffect(() => {
    if (status === 'active' && !timerRef.current) {
      timerRef.current = setInterval(() => setDurationSec((d) => d + 1), 1000);
    }
    if (status !== 'active' && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status]);

  const start = useCallback(async () => {
    // Ignore a second start while one is already running (e.g. a fast double-click before the
    // dialer's backdrop mounts): two concurrent inits would race into two Twilio Devices.
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    setError(null);
    setDurationSec(0);
    setIsMuted(false);
    setIsRecording(false);
    setVoiceCallId(null);
    setCallerNumber(null);
    setStatus('connecting');
    try {
      const { token } = await fetchToken();
      await client.init(token);
      const server = await startServerCall();
      setVoiceCallId(server.voiceCallId);
      setIsRecording(server.recordingEnabled ?? false);
      setCallerNumber(server.callerNumber);
      await client.dial({
        destinationNumber: server.destinationNumber,
        callerNumber: server.callerNumber,
        voiceCallId: server.voiceCallId,
      });
    } catch (e) {
      if (e instanceof CallAbortedError) {
        // Benign: the surface already handled it (toast + advanced). Reset quietly to idle so the
        // NEXT contact's dialer starts clean instead of inheriting this call's error state.
        setStatus('idle');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to start call');
        setStatus('error');
      }
    } finally {
      startInFlightRef.current = false;
    }
  }, [client, fetchToken, startServerCall]);

  const hangup = useCallback(() => {
    client.hangup();
  }, [client]);

  // Closing the dialer: end any live call (covers the outcome-screen path where the underlying
  // call can still be connected) AND return the controller to idle, so the next call starts
  // clean instead of inheriting the prior call's `ended`/`error` status and stale voiceCallId.
  const close = useCallback(() => {
    client.closeCall();
    setStatus('idle');
    setError(null);
    setDurationSec(0);
    setIsMuted(false);
    setIsRecording(false);
    setVoiceCallId(null);
    setCallerNumber(null);
  }, [client]);

  const toggleMute = useCallback(() => {
    const next = !isMuted;
    setIsMuted(next);
    // Run the side effect OUTSIDE the state updater (updaters must be pure) and guard it: a
    // provider whose mute() throws must not bubble an exception through the render/state path,
    // which would unmount the dialer and trigger its cleanup `client.destroy()` → disconnect —
    // i.e. muting would silently hang up the live call. Surface it as an error instead.
    try {
      client.setMuted(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to toggle mute');
    }
  }, [client, isMuted]);

  const sendDigits = useCallback(
    (digits: string) => {
      // Same guard rationale as toggleMute: Twilio's sendDigits throws on invalid characters or
      // before media is up, and an exception through the render path would unmount the dialer and
      // destroy the client — silently dropping the live call. Surface it as an error instead.
      try {
        client.sendDigits(digits);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to send digits');
      }
    },
    [client],
  );

  return {
    status,
    durationSec,
    isMuted,
    error,
    isRecording,
    voiceCallId,
    callerNumber,
    start,
    hangup,
    close,
    toggleMute,
    sendDigits,
  };
}
